import { MetricKey } from './risk.types';

// The BTC-settled book and the stablecoin-settled book(s) Deribit segregates
// per settlement currency. For USD-denominated (QUOTE) accounts these are
// summed into one USD figure — see getCombinedUsdSymbols / sumAlignedSeries.
export const BTC_USD_SYMBOL = 'BTCUSD';
// Stablecoin settlement books carry the deposited cash. In the combined view a
// stablecoin book contributes its PV (balance) to the total but zero risk —
// every greek is summed from the non-stablecoin (BTC) book only. USDe (Ethena)
// carries depeg/funding risk these greeks don't capture, but its balance still
// belongs in the capital total, so it is treated as cash here too.
export const STABLECOIN_USD_SYMBOLS = ['USDCUSD', 'USDTUSD', 'USDEUSD'] as const;

// Sentinel "token" for the synthesized combined-USD view. It is not a real
// exchange symbol, so it never goes on the wire — the hook expands it to its
// constituent symbols. Kept space-free so it survives the selector's
// space-stripping without being mistaken for a real pair.
export const COMBINED_USD_TOKEN = 'COMBINED_USD';
export const COMBINED_USD_LABEL = 'Combined USD';

export const AVAILABLE_METRICS: { value: MetricKey; label: string }[] = [
  { value: 'PV', label: 'PV' },
  { value: 'DELTA', label: 'Delta' },
  { value: 'GAMMA', label: 'Gamma' },
  { value: 'THETA', label: 'Theta' },
  { value: 'VEGA', label: 'Vega' },
];

export const RANGE_KEYS = ['1D', '1W', '1M', '3M'] as const;

export type RangeKey = (typeof RANGE_KEYS)[number];

export const RANGE_OPTIONS = RANGE_KEYS.map((value) => ({ name: value, value }));

const DAY_SECONDS = 24 * 60 * 60;

export const WINDOW_SECONDS: Record<RangeKey, number> = {
  '1D': DAY_SECONDS,
  '1W': 7 * DAY_SECONDS,
  '1M': 30 * DAY_SECONDS,
  '3M': 90 * DAY_SECONDS,
};

// LOCKSTEP: must agree with computeTimeRange's frequency ladder (risk.helpers)
// for these RangeKeys. The initial grid uses computeTimeRange, the historical-
// append grid uses frequencySecondsFor, and the combined-USD sum only aligns
// book-to-book when both frequencies match for a given range.
export const FREQUENCY_SECONDS: Record<RangeKey, number> = {
  '1D': 60,
  '1W': 900,
  '1M': 3600,
  '3M': 7200,
};

const DEFAULT_RANGE: RangeKey = '1D';

const isRangeKey = (range: string): range is RangeKey =>
  (RANGE_KEYS as readonly string[]).includes(range);

/** Window size in seconds for a range, falling back to the default range if unrecognised. */
export const windowSecondsFor = (range: string): number =>
  WINDOW_SECONDS[isRangeKey(range) ? range : DEFAULT_RANGE];

/** Sampling frequency in seconds for a range, falling back to the default range if unrecognised. */
export const frequencySecondsFor = (range: string): number =>
  FREQUENCY_SECONDS[isRangeKey(range) ? range : DEFAULT_RANGE];
